import React, { useEffect, useState } from 'react';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { DatePicker, StepWrapper, StepperControls, StyledCardSection } from '@fnol/library/src/components';
import {
    GenericObject,
    ScreenProps,
    checkUCN,
    common,
    fiveCalendarYearsFromNow,
    formatDate,
    scrollToFirstError,
} from '@fnol/library/src/lib';
import { Field, useFormikContext } from 'formik';
import { policiesProperties, question15MaxLength, translations } from '../../lib/consts';
import { prepareRisksList } from '../../lib/helpers';
import { getPolicyDetails, getPolicyRisks } from '../../rpc/actions';
import { useApi } from '@fnol/library/src/hooks';
import { Info } from '@mui/icons-material';
import './policy.css';

interface FormData {
    policyNumber: string;
    policyNumberOrUCN: string;
    policyUCN: string;
    dateOfOccurrence: string;
    validFrom: string;
    validTo: string;
    policyNotFound: string;
    risks: [GenericObject];
    risk: GenericObject;
    resetProcess: boolean;
}

const PolicyDetailScreen = ({ navigator }: ScreenProps) => {
    const [currentStep, { goForward, goBack }] = navigator;
    const { values, errors, touched, setErrors, setTouched, setFieldValue } = useFormikContext<FormData>();
    const [riskInfo, setRiskInfo] = useState('');

    let dataForDetails: GenericObject = {};

    const savePolicyDetails = (res: GenericObject) => {
        if (res.data.poruka === '') {
            setFieldValue('policyNumber', res.data[policiesProperties.policyNumber]);
            setFieldValue('validFrom', res.data[policiesProperties.validFrom]);
            setFieldValue('validTo', res.data[policiesProperties.validTo]);
            setFieldValue('numberOfWaitingPeriodDays', res.data[policiesProperties.numberOfWaitingPeriodDays]);
            setFieldValue('numberOfWaitingDays', res.data[policiesProperties.numberOfWaitingDays]);
            goForward();
        } else {
            setFieldValue('policyNotFound', res.data[policiesProperties.message]);
        }
    };

    const { response: policyDetails, fetchMethod: fetchPolicyDetails } = useApi(
        () => getPolicyDetails(dataForDetails),
        savePolicyDetails,
    );

    const savePolicyRisks = (res: GenericObject) => {
        setFieldValue('risks', prepareRisksList(res.data));
    };

    const { response: risks, fetchMethod: fetchPolicyRisks } = useApi(() => getPolicyRisks(), savePolicyRisks);

    const clearError = () => {
        setFieldValue('policyNotFound', '');
    };

    const handleNext = (): void => {
        setFieldValue('policyNotFound', '');
        setTouched(
            {
                dateOfOccurrence: true,
                policyNumberOrUCN: true,
                risk: true as any,
            },
            false,
        );

        const existingErrors: GenericObject = {};
        let valid = true;

        if (!values.policyNumberOrUCN) {
            existingErrors.policyNumberOrUCN = translations.validationMessages.REQUIRED_FIELD_POLICY_NO_UCN;
            valid = false;
        } else if (values.policyNumberOrUCN.length === 13 && !checkUCN(values.policyNumberOrUCN)) {
            existingErrors.policyNumberOrUCN = translations.validationMessages.UCN_INVALID_FORMAT;
            valid = false;
        }

        if (!values.dateOfOccurrence) {
            existingErrors.dateOfOccurrence = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (!values.risk) {
            existingErrors.risk = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        setTimeout(() => {
            setErrors(existingErrors);
            scrollToFirstError(existingErrors);
        });

        if (valid) {
            if (values.policyNumberOrUCN.length !== 13) {
                setFieldValue('policyNumber', values.policyNumberOrUCN);
                setFieldValue('policyUCN', '');
            } else {
                setFieldValue('policyNumber', '');
                setFieldValue('policyUCN', values.policyNumberOrUCN);
            }

            dataForDetails = {
                BrojPolise: values.policyNumberOrUCN.length !== 13 ? values.policyNumberOrUCN : '',
                JMBG: values.policyNumberOrUCN.length === 13 ? values.policyNumberOrUCN : '',
                RizikId: values.risk['id'],
                DatumNastanka: values.dateOfOccurrence !== '' ? formatDate(values.dateOfOccurrence) : '',
            };
            fetchPolicyDetails();
        }
    };

    const handleChangeRisk = (event: SelectChangeEvent<HTMLSelectElement>) => {
        const selectedRisk = values.risks.find((item: GenericObject) => item.id === event.target.value);
        setFieldValue('risk', selectedRisk);
        setRiskInfo(selectedRisk ? selectedRisk!['opis'] : '');
        setFieldValue('uploadDocList', []);
    };

    useEffect(() => {
        fetchPolicyRisks();
        if (values.risk && values.risk['opis']) {
            setRiskInfo(values.risk['opis']);
        }
        if (values.resetProcess) {
            setFieldValue('resetProcess', false);
            setFieldValue('policyNumber', '');
            setFieldValue('policyNumberOrUCN', '');
            setFieldValue('policyUCN', '');
            setFieldValue('dateOfOccurrence', null);
            setFieldValue('risk', '');
            setRiskInfo('');
        }
    }, []);

    return (
        <StepWrapper>
            <>
                <StyledCardSection>
                    <Typography style={{ fontWeight: 'bold', fontSize: '1.1rem' }} variant="caption">
                        {translations.POLICY}
                    </Typography>
                    <FormControl error={!!(touched.policyNumberOrUCN && errors.policyNumberOrUCN)}>
                        <Field
                            name="policyNumberOrUCN"
                            label={translations.POLICY_NUMBER_OR_UCN}
                            as={TextField}
                            inputProps={{
                                maxLength: question15MaxLength,
                            }}
                            error={!!(touched.policyNumberOrUCN && errors.policyNumberOrUCN)}
                            required
                        />
                        {touched.policyNumberOrUCN && errors.policyNumberOrUCN ? (
                            <FormHelperText error={true}>
                                <>{errors.policyNumberOrUCN}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                    <FormControl>
                        <InputLabel>{translations.RISKS}</InputLabel>
                        <Select
                            id="risk"
                            name="risk"
                            value={values.risk['id'] || ''}
                            label={translations.RISKS}
                            error={!!(touched.risk && errors.risk)}
                            onChange={handleChangeRisk}
                        >
                            {values.risks.map((option: any) => {
                                return (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.value}
                                    </MenuItem>
                                );
                            })}
                        </Select>

                        {touched.risk && errors.risk ? (
                            <FormHelperText error={true}>
                                <>{errors.risk}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                </StyledCardSection>
                <StyledCardSection>
                    <Typography style={{ fontWeight: 'bold', fontSize: '1.1rem' }} variant="caption">
                        {translations.OCCURRENCE_DATE}
                    </Typography>
                    {riskInfo != '' && (
                        <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                            <Info color="primary" />
                            <Typography variant="body2" paddingTop={0.5} style={{ fontWeight: 'bold' }}>
                                {riskInfo}
                            </Typography>
                        </Stack>
                    )}
                    <FormControl
                        error={!!(touched.dateOfOccurrence && errors.dateOfOccurrence)}
                        className="policyDatePicker"
                    >
                        <Field
                            name="dateOfOccurrence"
                            label={translations.DATE}
                            as={DatePicker}
                            disableFuture
                            minDate={fiveCalendarYearsFromNow}
                            error={!!(touched.dateOfOccurrence && errors.dateOfOccurrence)}
                            onChangeDate={clearError}
                        />
                        {touched.dateOfOccurrence && errors.dateOfOccurrence ? (
                            <FormHelperText error={true}>
                                <>{errors.dateOfOccurrence}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                </StyledCardSection>
                {values.policyNotFound !== '' && (
                    <FormHelperText error={true}>
                        <>{values.policyNotFound}</>
                    </FormHelperText>
                )}
            </>
            <StepperControls onNext={handleNext} onNextLabel={common.NEXT} idNext="policyDetailScreenNext" />
        </StepWrapper>
    );
};

export default PolicyDetailScreen;
